/**
 * Users waiting on a seat, as Needs attention rows (WI 34293 x WI 34329).
 *
 * The exceptions-first layout has no separate "Users waiting" tile, so a user
 * inbound still open past 2 minutes becomes an alert: a live wait of 10
 * minutes or more is critical, shorter live waits are warnings. A thread a
 * seat parked with a reason is listed (the reason is the detail) but is not a
 * live wait: it never goes critical and is not counted on the tile. A thread
 * parked on Tim is left to the Needs you card, which already lists it.
 */
import type { Alert, AlertAction } from './attention.logic';
import { type SeatSessionLink, seatSessionQuery } from './seat-session.logic';
import {
  UnansweredInbound,
  formatDuration,
  teamsChatDeepLink,
} from './wallboard.logic';

/** A live wait this long is critical (the old tile's red threshold). */
export const WAITING_CRIT_MINUTES = 10;

const NAMES_TIM = /\btim\b/i;

/** `sessions` (WI 34335) gives each row a link to the seat's own terminal; a seat not in it links with unknown host and session. */
export function waitingAlerts(
  rows: readonly UnansweredInbound[],
  sessions?: ReadonlyMap<string, SeatSessionLink>
): Alert[] {
  const out: Alert[] = [];
  for (const u of rows) {
    const session = sessions?.get(u.instanceId) ?? {
      seat: u.instanceId,
      host: null,
      session: null,
    };
    const parked = u.parkedReason !== null;
    if (parked && NAMES_TIM.test(u.parkedReason ?? '')) continue;
    const chatLink = teamsChatDeepLink(u.channel, u.chatId);
    const action: AlertAction = chatLink
      ? { type: 'open-chat', label: 'Open chat', target: chatLink }
      : { type: 'open-seat', label: 'Open seat', target: u.instanceId };
    const wait = formatDuration(u.waitingMinutes * 60_000);
    out.push({
      id: `waiting|${u.instanceId}|${u.chatId}`,
      severity:
        !parked && u.waitingMinutes >= WAITING_CRIT_MINUTES ? 'crit' : 'warn',
      kind: 'user',
      category: 'waiting',
      title: `${u.senderName} waiting on ${u.instanceId}`,
      detail: parked
        ? `Parked: ${u.parkedReason} · ${wait}`
        : `${u.channel} · waiting ${wait}`,
      action,
      weight: parked ? 0 : u.waitingMinutes,
      parked,
      dismiss: { chatId: u.chatId, receivedAtMs: u.receivedAtMs },
      session,
      sessionQuery: seatSessionQuery(session),
    });
  }
  return out;
}
